import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  useTheme
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  SentimentDissatisfied as SadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  LockPerson as LockPersonIcon
} from '@mui/icons-material';

/**
 * Standardized error state component for consistent error UX
 * 
 * @param {Object} props - Component props
 * @param {'error'|'warning'|'info'|'empty'} props.severity - Type of error/state
 * @param {string|React.ReactNode} props.title - Error title
 * @param {string|React.ReactNode} props.message - Error message or description
 * @param {Function} props.onRetry - Retry function for recoverable errors
 * @param {string} props.retryText - Custom retry button text
 * @param {React.ReactNode} props.action - Custom action button/component
 * @param {React.ReactNode} props.icon - Custom icon (overrides default)
 * @param {boolean} props.contained - Whether to wrap in Paper container
 * @param {number|object} props.padding - Padding around content
 * @param {object} props.sx - Additional sx props
 * @returns {React.ReactElement} The rendered ErrorState component
 */
const ErrorState = ({ 
  severity = 'error',
  title,
  message,
  onRetry,
  retryText = 'Try Again',
  action,
  icon,
  contained = true,
  padding = 4,
  sx = {}
}) => {
  const theme = useTheme();
  
  // Icon mapping based on severity
  const severityIcons = {
    error: <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />,
    warning: <WarningIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />,
    info: <InfoIcon sx={{ fontSize: 60, color: 'info.main', mb: 2 }} />,
    empty: <SadIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
  };
  
  const displayIcon = icon || severityIcons[severity];
  
  // Default messages based on severity
  const defaultMessages = {
    error: 'Something went wrong. Please try again.',
    warning: 'Please check your input and try again.',
    info: 'Additional information is needed.',
    empty: 'No items found.'
  };
  
  const defaultTitles = {
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
    empty: 'Nothing Here'
  };
  
  const displayTitle = title || defaultTitles[severity];
  const displayMessage = message || defaultMessages[severity];
  
  const content = (
    <Box
      sx={{
        textAlign: 'center',
        p: padding,
        ...sx
      }}
    >
      {displayIcon}
      
      <Typography 
        variant="h6" 
        color={severity === 'empty' ? 'text.secondary' : `${severity}.main`}
        gutterBottom
      >
        {displayTitle}
      </Typography>
      
      <Typography 
        variant="body1" 
        color="text.secondary" 
        paragraph
        sx={{ maxWidth: 400, mx: 'auto' }}
      >
        {displayMessage}
      </Typography>
      
      {(onRetry || action) && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          {onRetry && (
            <Button
              variant="contained"
              color={severity === 'error' ? 'primary' : severity}
              startIcon={<RefreshIcon />}
              onClick={onRetry}
            >
              {retryText}
            </Button>
          )}
          {action}
        </Box>
      )}
    </Box>
  );
  
  return contained ? (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      {content}
    </Paper>
  ) : (
    content
  );
};

/**
 * Predefined error states for common use cases
 */
export const NetworkError = (props) => (
  <ErrorState
    severity="error"
    title="Connection Error"
    message="Unable to connect to the server. Please check your internet connection and try again."
    retryText="Retry"
    {...props}
  />
);

export const NotFoundError = (props) => (
  <ErrorState
    severity="empty"
    title="Page Not Found"
    message="The page you're looking for doesn't exist or has been moved."
    {...props}
  />
);

export const EmptyListState = ({ itemType = 'items', createAction, ...props }) => (
  <ErrorState
    severity="empty"
    title={`No ${itemType} found`}
    message={`You haven't created any ${itemType} yet. Get started by creating your first ${itemType.slice(0, -1)}.`}
    action={createAction}
    {...props}
  />
);

export const PermissionError = ({ deniedPermission, resourcePath, onRequestAccess, ...props }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Lazy load the dialog to avoid circular dependencies
  const RequestAccessDialog = React.lazy(() => import('./RequestAccessDialog'));

  const formatPermission = (permission) => {
    if (!permission) return null;
    const parts = permission.split(':');
    if (parts.length === 2) {
      return `${parts[0]} (${parts[1]} access)`;
    }
    return permission;
  };

  const handleRequestAccess = () => {
    if (onRequestAccess) {
      onRequestAccess();
    } else {
      setDialogOpen(true);
    }
  };

  const message = (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        You don't have permission to access this resource.
      </Typography>
      {deniedPermission && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Required permission:
          </Typography>
          <Chip
            label={formatPermission(deniedPermission)}
            color="warning"
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}
    </Box>
  );

  const requestButton = deniedPermission ? (
    <Button
      variant="contained"
      color="primary"
      startIcon={<LockPersonIcon />}
      onClick={handleRequestAccess}
    >
      Request Access
    </Button>
  ) : null;

  return (
    <>
      <ErrorState
        severity="warning"
        title="Access Denied"
        message={message}
        action={requestButton}
        {...props}
      />
      {deniedPermission && (
        <React.Suspense fallback={<div />}>
          <RequestAccessDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            deniedPermission={deniedPermission}
            resourcePath={resourcePath}
          />
        </React.Suspense>
      )}
    </>
  );
};

export default ErrorState;